import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';
import { t } from '../lib/i18n';

/** Exit animation duration (matches --leave in the styles). */
export const LEAVE_MS = 170;

/** On close, play the exit animation before removing (same as 神探 (Shentan)'s Presence). */
export function Presence({ show, children }: { show: boolean; children: ReactNode }) {
  const [mounted, setMounted] = useState(show);
  const last = useRef<ReactNode>(children);
  if (show) last.current = children;
  useEffect(() => {
    if (show) {
      setMounted(true);
      return;
    }
    const t = setTimeout(() => setMounted(false), LEAVE_MS);
    return () => clearTimeout(t);
  }, [show]);
  if (!show && !mounted) return null;
  return (
    <div className="presence" data-state={show ? 'open' : 'closed'} style={{ display: 'contents' }}>
      {show ? children : last.current}
    </div>
  );
}

/** Dialog: a translucent deep-indigo backdrop + a sticker card slapped on top. Esc closes it; focus moves into the card. */
export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  className = '',
  labelledBy,
  dismissable = true,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  labelledBy?: string;
  dismissable?: boolean;
}) {
  const card = useRef<HTMLDivElement>(null);
  // The latest onClose without re-running the effect: callers pass a new function on every render, and re-running it would move
  // the focus back to the first field (scrolling a long page, like the AI settings, back to the top) whenever the app re-renders
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const t = setTimeout(() => {
      const el = card.current?.querySelector<HTMLElement>('[data-autofocus], input:not([type=hidden]), textarea, select, button.btn--primary');
      (el ?? card.current)?.focus();
    }, 30);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissable) {
        e.stopPropagation();
        close.current();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener('keydown', onKey, true);
      prev?.focus?.();
    };
  }, [open, dismissable]);
  return createPortal(
    <Presence show={open}>
      <div className="dialog" role="presentation">
        <button className="dialog__scrim" aria-label={t('关闭')} tabIndex={-1} onClick={() => dismissable && onClose()} />
        <div ref={card} className={`dialog__card ${className}`} role="dialog" aria-modal="true" aria-labelledby={labelledBy} tabIndex={-1}>
          {title !== undefined && (
            <header className="dialog__head">
              <h2 id={labelledBy}>{title}</h2>
              {dismissable && (
                <button className="btn btn--icon btn--quiet dialog__x" aria-label={t('关闭')} title={t('关闭（Esc）')} onClick={onClose}>
                  <Icon name="x" size={16} />
                </button>
              )}
            </header>
          )}
          <div className="dialog__body">{children}</div>
          {footer && <footer className="dialog__foot">{footer}</footer>}
        </div>
      </div>
    </Presence>,
    document.body,
  );
}

export interface ConfirmOptions {
  title: string;
  message: ReactNode;
  confirm: string;
  cancel?: string;
  danger?: boolean;
  /** The third option when there are three (e.g. "不保存" (don't save)). */
  alt?: string;
}

type ConfirmState = (ConfirmOptions & { resolve: (v: 'confirm' | 'alt' | 'cancel') => void }) | null;
let openConfirm: ((o: ConfirmState) => void) | null = null;

/** Show a confirm dialog and return which option the user chose. */
export function confirmDialog(options: ConfirmOptions): Promise<'confirm' | 'alt' | 'cancel'> {
  return new Promise((resolve) => {
    if (!openConfirm) return resolve(window.confirm(`${options.title}`) ? 'confirm' : 'cancel');
    openConfirm({ ...options, resolve });
  });
}

export function ConfirmHost() {
  const [state, setState] = useState<ConfirmState>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    openConfirm = (s) => {
      setState(s);
      setShown(true);
    };
    return () => {
      openConfirm = null;
    };
  }, []);
  const done = (v: 'confirm' | 'alt' | 'cancel') => {
    state?.resolve(v);
    setShown(false);
  };
  return (
    <Dialog
      open={shown}
      onClose={() => done('cancel')}
      title={state?.title ?? ''}
      labelledBy="confirm-title"
      className="dialog__card--small"
      footer={
        <>
          <button className="btn btn--quiet" onClick={() => done('cancel')}>
            {state?.cancel ?? t('取消')}
          </button>
          {state?.alt && (
            <button className="btn btn--quiet" onClick={() => done('alt')}>
              {state.alt}
            </button>
          )}
          <button className={`btn ${state?.danger ? 'btn--danger' : 'btn--primary'}`} data-autofocus onClick={() => done('confirm')}>
            {state?.confirm}
          </button>
        </>
      }
    >
      <div className="confirm__message">{state?.message}</div>
    </Dialog>
  );
}

/** Small popup menu: hangs below a button; closes on outside click or Esc. */
export function Popover({ open, onClose, children, align = 'left', className = '' }: { open: boolean; onClose: () => void; children: ReactNode; align?: 'left' | 'right'; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) && !(e.target as HTMLElement).closest('[data-popover-anchor]')) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [open, onClose]);
  // Keep it on screen for the exit animation after it closes
  const [shown, setShown] = useState(open);
  useEffect(() => {
    if (open) {
      setShown(true);
      return;
    }
    const timer = window.setTimeout(() => setShown(false), 130);
    return () => window.clearTimeout(timer);
  }, [open]);
  if (!open && !shown) return null;
  return (
    <div ref={ref} className={`popover popover--${align} ${open ? '' : 'popover--closing'} ${className}`} role="menu" aria-hidden={!open || undefined}>
      {children}
    </div>
  );
}
